import { LandThumbnail, LandThumbnailSkin, useFigures, useReasonMessages } from '@cibo/ui'
import {
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { indexBy, pluck, prop } from 'ramda'

import { useFields } from '@cibo/landmanager'
import { useTranslation } from 'react-i18next'
import { ConfirmIneligibleDialogProps } from '../ConfirmIneligibleDialogTypes'

const MAX_DISPLAY_ROWS = 3

export const FieldReasonsTable = ({ reasons }: Pick<ConfirmIneligibleDialogProps, 'reasons'>) => {
  const { t } = useTranslation('@cibo/programs/FieldReasonsTable')
  const resourceIds = reasons && (pluck('id', reasons) as string[])
  const fields = useFields(resourceIds)
  const reasonsByField = reasons && indexBy(prop('id'), reasons)
  const additionalFields = Math.max(0, (fields.data?.length ?? 0) - MAX_DISPLAY_ROWS)
  const { byReasonCode } = useReasonMessages()
  const { acres } = useFigures()

  return (
    <>
      {!!additionalFields && (
        <Typography>{t('additionalFields', { count: additionalFields })}</Typography>
      )}
      <Table size="small" sx={{ mt: 2, mb: 1 }}>
        <TableHead sx={visuallyHidden}>
          <TableRow>
            <TableCell>{t('fieldThumbnail')}</TableCell>
            <TableCell>{t('fieldName')}</TableCell>
            <TableCell>{t('fieldIneligibilityReasons')}</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {fields.data?.slice(0, MAX_DISPLAY_ROWS).map(field => {
            const reasons = reasonsByField?.[field.resourceId].reasons

            return (
              <TableRow
                key={field.resourceId}
                sx={{ '& .MuiTableCell-root': { borderBottom: 'none' } }}
              >
                <TableCell>
                  <LandThumbnail
                    landProfile={field}
                    width={60}
                    height={60}
                    skin={LandThumbnailSkin.SMALL}
                  />
                </TableCell>
                <TableCell>
                  <ListItemText
                    sx={{ mr: 2 }}
                    primary={field.name}
                    secondary={acres(field.acres)}
                  />
                </TableCell>

                <TableCell>
                  <ul style={{ listStyle: 'disc' }}>
                    {reasons?.map(({ reason, traitId }) => (
                      <li key={reason}>{byReasonCode({ reasonCode: reason, traitId })}</li>
                    ))}
                  </ul>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </>
  )
}
